import React, { useEffect } from 'react';
import { Navbar } from '../../../components/feature/Navbar';
import { Footer } from '../../../components/feature/Footer';

export default function SEOServicesPage() {
  useEffect(() => {
    document.title = 'SEO Optimization Services | Search Engine & AI Search Optimization | BusinessWebsitePro';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional SEO services for businesses in Canada, USA, and worldwide. Improve your Google rankings and AI search visibility. Get more organic traffic and customers with expert SEO optimization.');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[75vh] max-h-[85vh] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://static.readdy.ai/image/afbb870fba5009b6251518206686e578/2d22fa2dd98cdcab98ef4f1ec23cc841.png"
            alt="SEO optimization services"
            className="w-full h-full object-cover object-top"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>

        {/* Navbar */}
        <div className="absolute top-0 left-0 right-0 z-30">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="flex items-center justify-between">
              <a href="/" className="flex items-center gap-3 cursor-pointer">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg">
                  <i className="ri-window-line text-2xl text-white"></i>
                </div>
                <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  BusinessWebsitePro
                </span>
              </a>
              
              <nav className="hidden md:flex items-center gap-8">
                <a href="/" className="text-white hover:text-teal-400 transition-colors font-medium cursor-pointer">Home</a>
                <a href="/about" className="text-white hover:text-teal-400 transition-colors font-medium cursor-pointer">About</a>
                <a href="/services" className="text-white hover:text-teal-400 transition-colors font-medium cursor-pointer">Services</a>
                <a href="/portfolio" className="text-white hover:text-teal-400 transition-colors font-medium cursor-pointer">Portfolio</a>
                <a href="/pricing" className="text-white hover:text-teal-400 transition-colors font-medium cursor-pointer">Pricing</a>
                <a href="/blog" className="text-white hover:text-teal-400 transition-colors font-medium cursor-pointer">Blog</a>
                <a href="/contact" className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-6 py-2 rounded-full font-semibold hover:shadow-lg transition-all cursor-pointer whitespace-nowrap">
                  Contact Us
                </a>
              </nav>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-3xl p-8 sm:p-12 shadow-2xl" style={{ marginLeft: '8rem', maxWidth: '600px' }}>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 drop-shadow-lg" style={{ fontFamily: 'Poppins, sans-serif' }}>
              SEO Optimization Services
            </h1>
            <p className="text-xl text-white/90 mb-8 drop-shadow-lg">
              Advanced SEO and AI Search Optimization to help your business rank higher and get found by more customers online.
            </p>
            
            <div className="flex flex-wrap gap-4 justify-end" style={{ marginTop: '150px', marginRight: '75px' }}>
              <a
                href="/booking"
                className="group relative px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-semibold rounded-xl shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-calendar-check-line text-xl"></i>
                Book Free Consultation
              </a>
              <a
                href="/portfolio"
                className="group relative px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-xl border-2 border-white/30 hover:bg-white/20 hover:scale-105 transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-eye-line text-xl"></i>
                View Our Work
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Modern SEO Landscape */}
      <section className="py-20 bg-gradient-to-br from-indigo-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">The Modern SEO Landscape</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              SEO has evolved beyond traditional search engines. Today's optimization strategies must account for AI search, voice assistants, and changing user behaviors
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Traditional Search Engine Optimization</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Google remains the dominant search engine, processing over 8.5 billion searches daily. Our traditional SEO services ensure your website ranks prominently for keywords that matter to your business. We optimize every technical and content element to meet Google's evolving algorithm requirements.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                From comprehensive keyword research to technical site audits, we implement proven strategies that improve your search visibility. Our approach includes on-page optimization, content strategy, link building, and continuous monitoring to maintain and improve your rankings over time.
              </p>
              <div className="bg-gradient-to-br from-blue-50 to-white p-6 rounded-xl border border-blue-100">
                <h4 className="font-bold text-gray-900 mb-3">Key Focus Areas:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                    <span>Keyword targeting and content optimization for search intent</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                    <span>Technical SEO including site speed, mobile optimization, and crawlability</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                    <span>Authority building through quality backlinks and brand mentions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                    <span>Local SEO for businesses serving specific geographic areas</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">AI Search Optimization (AIO)</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                The rise of AI search engines like ChatGPT, Perplexity, and Google's AI Overviews is transforming how people find information. These platforms don't just return links – they provide direct answers by synthesizing information from multiple sources. Your business needs to be part of these AI-generated responses.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                AI Search Optimization (AIO) requires a different approach than traditional SEO. We structure your content to be easily understood and cited by AI systems, using semantic markup, clear information hierarchy, and authoritative content that AI engines trust and reference in their responses.
              </p>
              <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border border-purple-100">
                <h4 className="font-bold text-gray-900 mb-3">AIO Strategies:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                    <span>Structured data markup for AI comprehension and citation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                    <span>Natural language content optimized for conversational queries</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                    <span>Entity-based optimization to establish topical authority</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                    <span>FAQ and Q&A content that AI systems can easily reference</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-teal-500 to-cyan-500 p-8 rounded-2xl text-white">
            <div className="flex items-start gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                  <i className="ri-lightbulb-line text-3xl"></i>
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-3">Why Both SEO and AIO Matter</h3>
                <p className="text-white/90 leading-relaxed">
                  While AI search is growing rapidly, traditional search engines still drive the majority of web traffic. A comprehensive strategy addresses both channels, ensuring your business is visible wherever potential customers are searching. Our integrated approach maximizes your online visibility across all search platforms, delivering more traffic, leads, and revenue for your business.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Local SEO Deep Dive */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Local SEO for Canadian & US Businesses</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Dominate local search results and attract nearby customers actively looking for your services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-2xl border border-green-200">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                  <i className="ri-map-pin-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Google Business Profile Optimization</h3>
              </div>
              <p className="text-gray-600 leading-relaxed mb-4">
                Your Google Business Profile is crucial for local visibility. We optimize every element including business information, categories, photos, posts, and reviews to maximize your presence in Google Maps and local search results.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 flex-shrink-0 mt-1"></i>
                  <span>Complete profile setup with accurate business information</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 flex-shrink-0 mt-1"></i>
                  <span>Strategic category selection for maximum visibility</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 flex-shrink-0 mt-1"></i>
                  <span>Regular posts and updates to maintain engagement</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 flex-shrink-0 mt-1"></i>
                  <span>Review management and response strategies</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-white p-8 rounded-2xl border border-orange-200">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center">
                  <i className="ri-building-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Local Citation Building</h3>
              </div>
              <p className="text-gray-600 leading-relaxed mb-4">
                Consistent business information across online directories strengthens your local SEO. We build and manage citations on relevant platforms to improve your local search rankings and make it easier for customers to find you.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-orange-600 flex-shrink-0 mt-1"></i>
                  <span>Listings on major directories (Yelp, Yellow Pages, etc.)</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-orange-600 flex-shrink-0 mt-1"></i>
                  <span>Industry-specific directory submissions</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-orange-600 flex-shrink-0 mt-1"></i>
                  <span>NAP (Name, Address, Phone) consistency across all platforms</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-orange-600 flex-shrink-0 mt-1"></i>
                  <span>Ongoing monitoring and updates as needed</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-2xl border border-blue-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Cities We Serve Across North America</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 text-center">
              {[
                'Vancouver, BC', 'Toronto, ON', 'Montreal, QC', 'Calgary, AB',
                'Edmonton, AB', 'Ottawa, ON', 'Winnipeg, MB', 'Quebec City, QC',
                'New York, NY', 'Los Angeles, CA', 'Chicago, IL', 'Houston, TX',
                'Phoenix, AZ', 'Philadelphia, PA', 'San Antonio, TX', 'San Diego, CA',
                'Dallas, TX', 'San Jose, CA', 'Austin, TX', 'Jacksonville, FL',
                'Seattle, WA', 'Denver, CO', 'Boston, MA', 'Portland, OR'
              ].map((city, index) => (
                <div key={index} className="bg-white p-3 rounded-lg border border-blue-200 text-sm font-semibold text-gray-700">
                  {city}
                </div>
              ))}
            </div>
            <p className="text-center text-gray-600 mt-6">
              And hundreds more cities across Canada, United States, and worldwide
            </p>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Comprehensive SEO Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to rank higher and attract more organic traffic
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-2xl border border-blue-100">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-search-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Keyword Research</h3>
              <p className="text-gray-600 leading-relaxed">
                In-depth keyword analysis to identify high-value search terms your customers are using
              </p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-2xl border border-green-100">
              <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-file-text-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">On-Page Optimization</h3>
              <p className="text-gray-600 leading-relaxed">
                Optimize title tags, meta descriptions, headers, and content for maximum search visibility
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-white p-8 rounded-2xl border border-purple-100">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-code-s-slash-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Technical SEO</h3>
              <p className="text-gray-600 leading-relaxed">
                Fix technical issues, improve site speed, mobile optimization, and structured data markup
              </p>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-white p-8 rounded-2xl border border-orange-100">
              <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-article-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Content Optimization</h3>
              <p className="text-gray-600 leading-relaxed">
                Create and optimize high-quality content that ranks well and engages your audience
              </p>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-white p-8 rounded-2xl border border-teal-100">
              <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-map-pin-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Local SEO</h3>
              <p className="text-gray-600 leading-relaxed">
                Optimize for local searches with Google Business Profile and location-based keywords
              </p>
            </div>

            <div className="bg-gradient-to-br from-yellow-50 to-white p-8 rounded-2xl border border-yellow-100">
              <div className="w-14 h-14 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-links-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Link Building</h3>
              <p className="text-gray-600 leading-relaxed">
                Build high-quality backlinks to increase domain authority and search rankings
              </p>
            </div>

            <div className="bg-gradient-to-br from-indigo-50 to-white p-8 rounded-2xl border border-indigo-100">
              <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-robot-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">AI Search Optimization</h3>
              <p className="text-gray-600 leading-relaxed">
                Optimize for ChatGPT, Perplexity, and other AI search engines for maximum visibility
              </p>
            </div>

            <div className="bg-gradient-to-br from-pink-50 to-white p-8 rounded-2xl border border-pink-100">
              <div className="w-14 h-14 bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-line-chart-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Analytics & Reporting</h3>
              <p className="text-gray-600 leading-relaxed">
                Track rankings, traffic, and conversions with detailed monthly performance reports
              </p>
            </div>

            <div className="bg-gradient-to-br from-cyan-50 to-white p-8 rounded-2xl border border-cyan-100">
              <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-user-search-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Competitor Analysis</h3>
              <p className="text-gray-600 leading-relaxed">
                Analyze competitor strategies and identify opportunities to outrank them
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Invest in SEO</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Long-term benefits that drive sustainable business growth
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center">
                  <i className="ri-money-dollar-circle-line text-2xl text-white"></i>
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Cost-Effective Marketing</h3>
                <p className="text-gray-600 leading-relaxed">
                  SEO delivers long-term results at a fraction of the cost of paid advertising. Organic traffic continues to grow over time without ongoing ad spend.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                  <i className="ri-user-add-line text-2xl text-white"></i>
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">More Qualified Leads</h3>
                <p className="text-gray-600 leading-relaxed">
                  Attract customers actively searching for your products or services. SEO brings in high-intent traffic that's more likely to convert.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                  <i className="ri-trophy-line text-2xl text-white"></i>
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Build Brand Authority</h3>
                <p className="text-gray-600 leading-relaxed">
                  Higher rankings establish your business as an industry leader. Customers trust businesses that appear at the top of search results.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                  <i className="ri-line-chart-line text-2xl text-white"></i>
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Sustainable Growth</h3>
                <p className="text-gray-600 leading-relaxed">
                  Unlike paid ads that stop when you stop paying, SEO results compound over time. Build a foundation for long-term business success.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-teal-500 to-cyan-500">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Rank Higher on Google?</h2>
          <p className="text-xl text-white/90 mb-8">
            Get more organic traffic and customers with expert SEO optimization. Start with a free SEO audit today.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a
              href="/contact"
              className="bg-white text-teal-600 px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-xl"
            >
              Get Free SEO Audit
            </a>
            <a
              href="/portfolio"
              className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white/20 transition-all duration-300 border-2 border-white cursor-pointer whitespace-nowrap"
            >
              View Our Work
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
